import { Badge, Box, Button, Container, HStack, Heading, IconButton, Menu, MenuButton, MenuList, Spinner, Text, Tooltip, useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import React from 'react';
import { AppContext } from '../comments/AppContext';
import { StorageService } from '../services/StorageService';
import { stringifyError } from '@/shared/parse';
import { MdEditor, ToolbarTips } from 'md-editor-rt';
import { WorkflowTextService } from '@/shared/WorkflowTextService';
import { AdminTemplateService } from './AdminTemplateService';
import { StorageCacheService } from '../services/StorageCacheService';
import { TEMPLATE } from '@/shared/constants';
import { ErrorMessage } from '../components/ErrorMessage';
import { FiMenu } from 'react-icons/fi';
import { TelemetryClientService } from '../services/TelemetryClientService';
import { AppBreadcrumb } from '../navbar/AppBreadcrumb';

export const EditlTemplatePage = () => {

  const params = useParams<{ templateKind: string, templateId: TEMPLATE }>();

  const { isAuthenticated } = React.useContext(AppContext);

  const toast = useToast();

  const [markdown, setMarkdown] = React.useState('Loading content...');
  const [errorMessage, setErrorMessage] = React.useState<any>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setErrorMessage(undefined);
    if (params.templateId) {
      setIsLoading(true);
      StorageService.getTemplateFile(params.templateId).then(md => {
        setMarkdown(md);
      }, (err) => {
        setErrorMessage(err);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [params.templateId]);

  const saveTemplate = async () => {
    if (params.templateId) {
      try {
        setIsSaving(true);
        await StorageService.putTemplateFile(params.templateId, markdown);
        StorageCacheService.resetCache('template:');
      } catch (err) {
        TelemetryClientService.error(`Error saving template ${params.templateId}`, err);
        toast({
          title: `Unable to save the template for ${params.templateId}`,
          description: stringifyError(err),
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsSaving(false);
      }
    }
  }

  const startWithUpperCase = (str?: string) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  const breadcrumb = React.useMemo(() => [
    { href: `/admin`, label: 'Admin' },
    { href: `/admin/${params.templateKind}`, label: startWithUpperCase(params.templateKind) },
    { href: `/admin/${params.templateKind}/${params.templateId}`, label: params.templateId },
  ], [params.templateKind, params.templateId]);

  const navigate = useNavigate();

  const onClose = () => {
    navigate(`/admin/${params.templateKind}`);
  }

  const templateParameters = AdminTemplateService.getTemplateParameters(params.templateKind);

  const toolbar: (keyof ToolbarTips)[] = [
    'bold',
    'underline',
    'italic',
    // 'strikeThrough',
    '-',
    'title',
    'quote',
    'unorderedList',
    'orderedList',
    '-',
    'link',
    'image',
    'table',
    '-',
    'revoke',
    'next',
    'save',
    '=',
    'prettier',
    'pageFullscreen',
    'fullscreen',
    'preview',
    'previewOnly',
    'htmlPreview',
    'catalog',
  ];

  const editorRef = React.useRef<any>(null);

  const onInsertTemplate = (text: string) => {
    editorRef.current?.insert(() => {
      /**
       * @return targetValue    Content to be inserted
       * @return select         Automatically select content, default: true
       * @return deviationStart Start position of the selected content, default: 0
       * @return deviationEnd   End position of the selected content, default: 0
       */
      return {
        targetValue: `{{{${text}}}}`,
        select: true,
        deviationStart: 0,
        deviationEnd: 0
      };
    });
  }

  const onUploadImg = async (files: File[], callback: (urls: string[] | { url: string; alt: string; title: string }[]) => void) => {
    try {
      const urls = await StorageService.putTemplateImageFiles(files);
      callback(urls);
    } catch (err) {
      toast({
        title: `Unable to upload the image`,
        description: stringifyError(err),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  }

  return <>
    <AppBreadcrumb breadcrumb={breadcrumb} />
    <Container mt={150} maxW={'full'} height='80vh'>
      <ErrorMessage error={errorMessage} />
      {isAuthenticated && <>
        <HStack spacing='4' justify='space-between'>
          <Heading size='sm'>{params.templateId}</Heading>
          <HStack spacing={2}>
            <Button isDisabled={isSaving || isLoading} variant='solid' onClick={saveTemplate}>
              {isSaving && <Spinner size='xs' mr='2' />}
              {isSaving ? 'Saving Page...' : 'Save Page'}
            </Button>
            <Button variant='outline' onClick={onClose}>Close</Button>
            <Menu>
              <MenuButton as={IconButton} isDisabled={true} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
              <MenuList>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
        <Box mb='4' color='fg.muted'>
          <Text>{WorkflowTextService.getTemplateDescription(params.templateId)}</Text>
          <Text>Available placeholders (click to insert at the cursor position):</Text>
          <HStack wrap='wrap' spacing={2}>
            {Object.entries(templateParameters).map(([key, value]) => (
              <Tooltip fontSize='small' key={key} label={value.description}>
                <Badge onClick={() => onInsertTemplate(key)} cursor='pointer' textTransform='none' colorScheme='gray'>{`{{{${key}}}}`}</Badge>
              </Tooltip>
            ))}
          </HStack>
        </Box>
        <MdEditor
          ref={editorRef}
          disabled={isSaving || isLoading}
          value={markdown}
          onChange={setMarkdown}
          onSave={saveTemplate}
          onUploadImg={onUploadImg}
          toolbars={toolbar}
          style={{ height: 'calc(100% - 200px)' }}
          language='en-US'
        />
      </>}
    </Container>
  </>
}